import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';

// Presentation mirrors the RaceVoiceApp "Video Studio": a reel-type pill
// selector (◀ TYPE ▶ + dots) over a peeking card carousel of clips. The web
// only plays already-generated videos, so there is no generate/share/save/delete
// — just selection and playback.

const styles = {
  container: {
    display: 'flex',
    gap: 16,
    flexWrap: 'wrap',
  },
  panel: {
    flex: '1 1 0',
    minWidth: 280,
  },
  panelLabel: {
    fontSize: 11,
    fontWeight: 700,
    fontFamily: 'var(--font-heading)',
    letterSpacing: 1,
    color: 'var(--color-secondary)',
    marginBottom: 10,
    textTransform: 'uppercase',
  },

  // ── Mode (reel type) selector ──
  modeRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  modeArrow: {
    flex: '0 0 auto',
    width: 36,
    height: 40,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'transparent',
    border: 'none',
    color: 'var(--color-accent)',
    fontSize: 18,
    lineHeight: 1,
    cursor: 'pointer',
    borderRadius: 6,
  },
  modeArrowDisabled: {
    opacity: 0.25,
    cursor: 'default',
  },
  modePill: {
    flex: '1 1 auto',
    textAlign: 'center',
    padding: '9px 12px',
    background: 'linear-gradient(135deg, #1a5276, #2e86c1)',
    border: '1px solid rgba(46,134,193,0.5)',
    borderRadius: 6,
    color: '#fff',
    fontFamily: 'var(--font-heading)',
    fontSize: 13,
    fontWeight: 700,
    letterSpacing: 1.5,
    textTransform: 'uppercase',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  dotsRow: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 6,
    margin: '8px 0',
    minHeight: 8,
  },
  dot: {
    width: 7,
    height: 7,
    borderRadius: '50%',
    background: 'var(--color-border)',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
  },
  dotActive: {
    background: 'var(--color-accent)',
  },

  // ── Clip carousel ──
  carouselWrap: {
    position: 'relative',
  },
  viewport: {
    position: 'relative',
    display: 'flex',
    gap: 12,
    overflowX: 'auto',
    padding: '4px 0 6px',
    scrollSnapType: 'x mandatory',
  },
  card: {
    flex: '0 0 88%',
    scrollSnapAlign: 'center',
    background: 'linear-gradient(145deg, #0a0a10, #12121c)',
    border: '1.5px solid var(--color-border)',
    borderRadius: 10,
    overflow: 'hidden',
    transition: 'border-color 0.2s ease, opacity 0.2s ease',
  },
  cardActive: {
    borderColor: 'var(--color-accent)',
    boxShadow: '0 6px 24px rgba(0,0,0,0.4)',
  },
  cardInactive: {
    opacity: 0.55,
  },
  cardVideo: {
    width: '100%',
    display: 'block',
    aspectRatio: '16 / 9',
    background: '#000',
    objectFit: 'contain',
  },
  cardBody: {
    padding: '10px 14px 13px',
  },
  cardTitle: {
    fontFamily: 'var(--font-heading)',
    fontSize: 14,
    fontWeight: 700,
    color: 'var(--color-text)',
    letterSpacing: 0.4,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  cardSub: {
    fontFamily: 'var(--font-data)',
    fontSize: 12,
    color: 'var(--color-text-dim)',
    marginTop: 3,
  },
  navArrow: {
    position: 'absolute',
    top: 'calc(50% - 24px)',
    transform: 'translateY(-50%)',
    width: 36,
    height: 36,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(6,6,9,0.7)',
    border: '1px solid var(--color-border)',
    color: '#fff',
    fontSize: 16,
    cursor: 'pointer',
    zIndex: 2,
    backdropFilter: 'blur(4px)',
    WebkitBackdropFilter: 'blur(4px)',
  },

  empty: {
    width: '100%',
    aspectRatio: '16 / 9',
    borderRadius: 10,
    background: 'rgba(13,27,42,0.6)',
    border: '1.5px solid var(--color-border)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'var(--color-text-dim)',
    fontSize: 13,
    fontFamily: 'var(--font-heading)',
    letterSpacing: 1,
    textTransform: 'uppercase',
  },
};

// Injected once: hide the carousel scrollbar while keeping native swipe/scroll.
const CAROUSEL_CSS = `
.rv-vp-viewport::-webkit-scrollbar { height: 0; width: 0; display: none; }
.rv-vp-viewport { -ms-overflow-style: none; scrollbar-width: none; }
`;

const MIME_MAP = {
  '.mp4': 'video/mp4',
  '.m4v': 'video/mp4',
  '.webm': 'video/webm',
  '.mov': 'video/mp4',
  '.avi': 'video/x-msvideo',
  '.mkv': 'video/x-matroska',
  '.ogv': 'video/ogg',
};

function getVideoMime(filename) {
  if (!filename) return '';
  const ext = filename.toLowerCase().match(/\.[a-z0-9]+$/)?.[0] || '';
  return MIME_MAP[ext] || '';
}

function normalizeVideoKey(video) {
  const source = video?.label || video?.filename || '';
  return String(source)
    .toLowerCase()
    .replace(/\.[a-z0-9]+$/i, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

function segmentOrder(key) {
  const match = key.match(/(?:hero_segment|hero|blooper|bloopers)(?:_|)(\d+)/);
  return match ? Number(match[1]) : Number.MAX_SAFE_INTEGER;
}

function videoSortValue(video) {
  const key = normalizeVideoKey(video);
  if (key === 'hero_lap' || key === 'hero_full') return [0, 0, key];
  if (key.startsWith('hero_segment') || /^hero_\d+/.test(key)) return [1, segmentOrder(key), key];
  if (key === 'bloopers' || key === 'blooper_full' || key === 'bloopers_full') return [2, 0, key];
  if (key.startsWith('blooper_') || key.startsWith('bloopers_')) return [2, segmentOrder(key), key];
  return [3, 0, key];
}

function compareVideos(a, b) {
  const aSort = videoSortValue(a);
  const bSort = videoSortValue(b);
  for (let i = 0; i < aSort.length; i += 1) {
    if (aSort[i] < bSort[i]) return -1;
    if (aSort[i] > bSort[i]) return 1;
  }
  return 0;
}

// Reel types, in the same order the app's Video Studio shows them.
const TYPE_DEFS = [
  { key: 'full', label: 'Full Session', test: (k) => k === 'full' || k === 'full_session' || k === 'raw' || k === 'session' || k === 'recording' },
  { key: 'best', label: 'Best Lap', test: (k) => k.includes('best') },
  { key: 'hero', label: 'Hero', test: (k) => k.startsWith('hero') },
  { key: 'blooper', label: 'Bloopers', test: (k) => k.startsWith('blooper') },
  { key: 'sidebyside', label: 'Side By Side', test: (k) => k.includes('sidebyside') || k.includes('side_by_side') },
];

function classifyType(video) {
  const key = normalizeVideoKey(video);
  const def = TYPE_DEFS.find((d) => d.test(key));
  return def ? def.key : 'other';
}

// Per-clip subtitle, mirroring the app's "N segments" / segment labelling.
function clipSubtitle(video, idx, total) {
  const key = normalizeVideoKey(video);
  const seg = key.match(/(?:hero_segment|hero|blooper|bloopers)_?(\d+)/);
  if (seg) return `Segment ${seg[1]}`;
  if (total > 1) return idx === 0 ? 'Full reel' : `Clip ${idx + 1}`;
  return '';
}

function VideoPanel({ videos, label }) {
  const [typeIndex, setTypeIndex] = useState(0);
  const [clipIndex, setClipIndex] = useState(0);
  const viewportRef = useRef(null);
  const cardRefs = useRef([]);
  const rafRef = useRef(0);

  // Group available videos into reel types, preserving TYPE_DEFS order.
  const types = useMemo(() => {
    const list = Array.isArray(videos) ? videos : [];
    const groups = new Map();
    for (const v of list) {
      const t = classifyType(v);
      if (!groups.has(t)) groups.set(t, []);
      groups.get(t).push(v);
    }
    const ordered = [];
    for (const def of TYPE_DEFS) {
      if (groups.has(def.key)) ordered.push({ key: def.key, label: def.label, clips: groups.get(def.key).sort(compareVideos) });
    }
    if (groups.has('other')) ordered.push({ key: 'other', label: 'Clips', clips: groups.get('other').sort(compareVideos) });
    return ordered;
  }, [videos]);

  const hasVideos = types.length > 0;
  const safeTypeIndex = Math.min(typeIndex, Math.max(0, types.length - 1));
  const clips = hasVideos ? types[safeTypeIndex].clips : [];

  // Reset to the first type/clip whenever the underlying videos change.
  useEffect(() => {
    setTypeIndex(0);
    setClipIndex(0);
  }, [types]);

  // On type change, snap the carousel back to the first clip.
  useEffect(() => {
    setClipIndex(0);
    if (viewportRef.current) viewportRef.current.scrollTo({ left: 0 });
  }, [safeTypeIndex]);

  const onViewportScroll = useCallback(() => {
    if (rafRef.current) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = 0;
      const vp = viewportRef.current;
      if (!vp) return;
      const center = vp.scrollLeft + vp.clientWidth / 2;
      let best = 0;
      let bestDist = Infinity;
      cardRefs.current.forEach((el, i) => {
        if (!el) return;
        const c = el.offsetLeft + el.offsetWidth / 2;
        const d = Math.abs(c - center);
        if (d < bestDist) { bestDist = d; best = i; }
      });
      setClipIndex(best);
    });
  }, []);

  const scrollToClip = useCallback((i) => {
    const el = cardRefs.current[i];
    if (el) el.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
  }, []);

  const goType = (delta) => {
    const next = safeTypeIndex + delta;
    if (next >= 0 && next < types.length) setTypeIndex(next);
  };

  return (
    <div style={styles.panel}>
      {label && <div style={styles.panelLabel}>{label}</div>}

      {/* Reel-type selector */}
      <div style={styles.modeRow}>
        <button
          type="button"
          style={{ ...styles.modeArrow, ...(safeTypeIndex <= 0 ? styles.modeArrowDisabled : {}) }}
          onClick={() => goType(-1)}
          disabled={safeTypeIndex <= 0}
          aria-label="Previous video type"
        >
          ◀
        </button>
        <div style={styles.modePill}>{hasVideos ? types[safeTypeIndex].label : 'No Video'}</div>
        <button
          type="button"
          style={{ ...styles.modeArrow, ...(safeTypeIndex >= types.length - 1 ? styles.modeArrowDisabled : {}) }}
          onClick={() => goType(1)}
          disabled={safeTypeIndex >= types.length - 1}
          aria-label="Next video type"
        >
          ▶
        </button>
      </div>

      {types.length > 1 && (
        <div style={styles.dotsRow}>
          {types.map((t, i) => (
            <button
              key={t.key}
              type="button"
              style={{ ...styles.dot, ...(i === safeTypeIndex ? styles.dotActive : {}) }}
              onClick={() => setTypeIndex(i)}
              aria-label={`Show ${t.label}`}
            />
          ))}
        </div>
      )}

      {/* Clip carousel */}
      {hasVideos ? (
        <div style={styles.carouselWrap}>
          {clips.length > 1 && clipIndex > 0 && (
            <button type="button" style={{ ...styles.navArrow, left: 6 }} onClick={() => scrollToClip(clipIndex - 1)} aria-label="Previous clip">‹</button>
          )}
          <div
            ref={viewportRef}
            className="rv-vp-viewport"
            style={styles.viewport}
            onScroll={onViewportScroll}
          >
            {clips.map((v, i) => (
              <div
                key={v.filename}
                ref={(el) => { cardRefs.current[i] = el; }}
                style={{
                  ...styles.card,
                  ...(i === clipIndex ? styles.cardActive : styles.cardInactive),
                  ...(clips.length === 1 ? { flexBasis: '100%' } : {}),
                }}
              >
                <video style={styles.cardVideo} controls preload="metadata">
                  <source src={v.url} type={getVideoMime(v.filename)} />
                  Your browser does not support this video format.
                </video>
                <div style={styles.cardBody}>
                  <div style={styles.cardTitle}>{v.label}</div>
                  {clipSubtitle(v, i, clips.length) && (
                    <div style={styles.cardSub}>{clipSubtitle(v, i, clips.length)}</div>
                  )}
                </div>
              </div>
            ))}
          </div>
          {clips.length > 1 && clipIndex < clips.length - 1 && (
            <button type="button" style={{ ...styles.navArrow, right: 6 }} onClick={() => scrollToClip(clipIndex + 1)} aria-label="Next clip">›</button>
          )}
        </div>
      ) : (
        <div style={styles.empty}>No video available</div>
      )}

      {hasVideos && clips.length > 1 && (
        <div style={styles.dotsRow}>
          {clips.map((v, i) => (
            <button
              key={v.filename}
              type="button"
              style={{ ...styles.dot, ...(i === clipIndex ? styles.dotActive : {}) }}
              onClick={() => scrollToClip(i)}
              aria-label={`Clip ${i + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function VideoPlayer({ primaryVideos, overlayVideos, overlayLabel, hasOverlaySession }) {
  return (
    <>
      <style>{CAROUSEL_CSS}</style>
      <div style={{ ...styles.container, ...(hasOverlaySession ? { flexDirection: 'column' } : {}) }}>
        <VideoPanel
          videos={primaryVideos}
          label={hasOverlaySession ? 'Primary Session' : null}
        />
        {hasOverlaySession && (
          <VideoPanel
            videos={overlayVideos}
            label={overlayLabel || 'Compare Session'}
          />
        )}
      </div>
    </>
  );
}
